// NEXUS Landing — Hero · Sponsored Spotlight · 4 Live Game Highlights
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Gamepad2, Coins, Check, Radio, Video, Shirt } from "lucide-react";
import api from "../lib/api";
import LivePreview from "../components/LivePreview";

const FALLBACK_THUMBS = {
  "Call of Duty: Warzone": "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=1200&q=70",
  "Apex Legends": "https://images.unsplash.com/photo-1538481199705-c710c4e965fc?auto=format&fit=crop&w=1200&q=70",
  "Fortnite": "https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&w=1200&q=70",
  "IRL": "https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?auto=format&fit=crop&w=1200&q=70",
  "Live": "https://images.unsplash.com/photo-1493711662062-fa541adb3fc8?auto=format&fit=crop&w=1200&q=70",
  "Music": "https://images.unsplash.com/photo-1470225620780-dba8ba36b745?auto=format&fit=crop&w=1200&q=70",
  "Just Chatting": "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1200&q=70",
  "Valorant": "https://images.unsplash.com/photo-1552820728-8b83bb6b773f?auto=format&fit=crop&w=1200&q=70",
  "GTA V": "https://images.unsplash.com/photo-1560253023-3ec5d502959f?auto=format&fit=crop&w=1200&q=70",
  "Minecraft": "https://images.unsplash.com/photo-1607853202273-797f1c22a38e?auto=format&fit=crop&w=1200&q=70",
  "League of Legends": "https://images.unsplash.com/photo-1527980965255-d3b416303d12?auto=format&fit=crop&w=1200&q=70",
};



export default function Landing() {
  const [settings, setSettings] = useState({});
  const [spotlight, setSpotlight] = useState([]);
  const [spotIdx, setSpotIdx] = useState(0);
  const [highlights, setHighlights] = useState([]);
  const [hlIdx, setHlIdx] = useState(0);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setSettings(data)).catch(() => {});
    api.get("/spotlight/sponsored").then(({ data }) => setSpotlight(data)).catch(() => setSpotlight([]));
    api.get("/highlights/v2").then(({ data }) => setHighlights(data)).catch(() => setHighlights([]));
    // Real category list + live counts -- was previously a stale hardcoded array here
    // that never matched what OBS streamers could actually pick, so newer categories
    // (Just Chatting, Podcast, Mobile Live, All Games, etc.) never showed up. Fetching
    // /game-categories means this row can never drift out of sync with go-live again.
    api.get("/game-categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
  }, []);

  // Twitch-style: on every hlIdx tick, refetch both endpoints so the highlights row and
  // the sponsored spotlight cycle through fresh content. Skip the very first tick (hlIdx=0)
  // because the mount effect above already loaded initial state.
  useEffect(() => {
    if (hlIdx === 0) return;
    api.get("/highlights/v2", { params: { rotate_idx: hlIdx } }).then(({ data }) => setHighlights(data)).catch(() => {});
    api.get("/spotlight/sponsored").then(({ data }) => setSpotlight(data)).catch(() => {});
  }, [hlIdx]);

  useEffect(() => {
    let t = null;
    const start = () => { if (!t) t = setInterval(() => setSpotIdx((i) => i + 1), 6000); };
    const stop = () => { if (t) { clearInterval(t); t = null; } };
    const onVis = () => { document.visibilityState === "visible" ? start() : stop(); };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  useEffect(() => {
    let t = null;
    const start = () => { if (!t) t = setInterval(() => setHlIdx((i) => i + 1), 8000); };
    const stop = () => { if (t) { clearInterval(t); t = null; } };
    const onVis = () => { document.visibilityState === "visible" ? start() : stop(); };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  return (
    <div className="space-y-20 pb-20" data-testid="landing-page">
      {/* === HERO === */}
      <section className="relative min-h-[40vh] lg:min-h-[46vh] -mx-6 lg:-mx-10 -mt-10 overflow-hidden flex items-center">
        <div className="absolute inset-0 nexus-bg-grid opacity-15 pointer-events-none" />
        <div className="relative w-full max-w-[1500px] mx-auto px-6 lg:px-10 pt-16 pb-6">
          <div className="flex items-center gap-5 lg:gap-8 flex-wrap" data-testid="hero-wordmark-block">
            <div className="nexus-hero-hex" data-testid="hero-hex-logo">
              <svg viewBox="0 0 100 100" className="w-[140px] h-[140px] sm:w-[180px] sm:h-[180px] lg:w-[240px] lg:h-[240px]">
                <defs>
                  <linearGradient id="hex-stroke" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FF2EA6" />
                    <stop offset="50%" stopColor="#8A2BE2" />
                    <stop offset="100%" stopColor="#00CFFF" />
                  </linearGradient>
                  <filter id="hex-glow"><feGaussianBlur stdDeviation="2.4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
                </defs>
                <polygon points="50,5 92,28 92,72 50,95 8,72 8,28" fill="none" stroke="url(#hex-stroke)" strokeWidth="4" filter="url(#hex-glow)" />
              </svg>
            </div>
            <div className="leading-[0.86]">
              <div className="font-display font-black text-6xl sm:text-8xl lg:text-[9rem] tracking-tighter text-white">NEXUS</div>
              <div className="font-display font-black text-5xl sm:text-7xl lg:text-[7.5rem] tracking-tighter nexus-gradient-text">LIVE</div>
              <div className="text-[10px] sm:text-xs tracking-[0.6em] uppercase text-white/40 mt-3 font-display">{settings.platform_tagline || "Streams · Stickers · Subs · Sponsors"}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-10" data-testid="hero-ctas">
            <Link to="/auth?mode=signup" className="btn-nexus" data-testid="cta-signup-free"><Sparkles className="w-3.5 h-3.5" />Sign up Free</Link>
            <Link to="/auth" className="btn-ghost" data-testid="cta-login">Sign in</Link>
          </div>
        </div>
      </section>

      {/* === SPONSORED SPOTLIGHT === */}
      <section data-testid="sponsored-spotlight" className="space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-eyebrow mb-2 text-nexus-gold flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" />// sponsored spotlight</div>
            <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter">Featured sponsored streamers.</h2>
          </div>
          <div className="hidden md:flex gap-2 text-[10px] tracking-[0.3em] uppercase text-white/40 font-display items-center">
            <span className="w-2 h-2 bg-nexus-gold animate-pulse" /> auto-rotates · {spotlight.length} channels
          </div>
        </div>
        {spotlight.length === 0 ? (
          <div className="aspect-[21/9] bg-nexus-surface/40 border border-white/10 flex items-center justify-center text-white/40 text-sm" data-testid="spotlight-empty">No sponsored creators yet.</div>
        ) : (() => {
          const s = spotlight[spotIdx % spotlight.length];
          const isLive = !!s.stream;
          // Click routes to the creator's profile so viewers can BOTH read about them AND
          // watch the embedded stream (Twitch-style profile+stream on one page).
          const target = `/u/${s.id}`;
          const thumb = isLive ? s.stream.thumbnail : s.avatar;
          return (
            <Link to={target} className="block relative aspect-[21/9] overflow-hidden border border-nexus-gold/40 group" data-testid="spotlight-card">
              <img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              {isLive && s.stream.livekit_room && <LivePreview streamId={s.stream.id} fallback={null} />}
              <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/35 to-transparent" />
              <div className="absolute inset-y-0 left-0 w-full md:w-1/2 flex flex-col justify-end p-6 lg:p-10 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="bg-nexus-gold text-nexus-obsidian px-2 py-1 text-[9px] tracking-[0.4em] font-display font-black uppercase">★ Sponsored</span>
                  {isLive
                    ? <span className="bg-nexus-crimson px-2 py-1 text-[9px] tracking-[0.3em] font-display font-bold uppercase animate-pulse">● LIVE</span>
                    : <span className="bg-white/10 px-2 py-1 text-[9px] tracking-[0.3em] font-display uppercase text-white/60">offline</span>}
                </div>
                <div className="font-display font-black text-3xl lg:text-5xl tracking-tighter group-hover:text-nexus-gold transition-colors">{s.display_name}</div>
                <div className="text-sm text-white/60 max-w-md line-clamp-2 leading-snug">{isLive ? s.stream.title : (s.bio || "Tap to view profile and recent activity.")}</div>
                {isLive && <div className="text-[11px] tracking-wider text-nexus-gold">{(s.stream.viewers || 0).toLocaleString()} watching now</div>}
              </div>
            </Link>
          );
        })()}
      </section>

      {/* === CATEGORIES WITH LIVE PREVIEWS === */}
      <section data-testid="main-highlights" className="space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-eyebrow mb-2 flex items-center gap-2"><Gamepad2 className="w-3.5 h-3.5" />// live categories</div>
            <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter">Browse categories. Watch live.</h2>
            <p className="text-white/50 mt-2 max-w-2xl text-sm">Auto-rotating live previews from every category on NEXUS — pick a game and jump straight in.</p>
          </div>
          <div className="hidden md:flex gap-2 text-[10px] tracking-[0.3em] uppercase text-white/40 font-display items-center">
            <span className="w-2 h-2 bg-nexus-cyan animate-pulse" /> previews refresh every 8s
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="highlights-grid">
          {(() => {
            const baseHighlights = highlights.length ? highlights : [
              { game: "Call of Duty: Warzone", stream: null, pool_size: 0 },
              { game: "Apex Legends", stream: null, pool_size: 0 },
              { game: "Fortnite", stream: null, pool_size: 0 },
              { game: "IRL", stream: null, pool_size: 0 },
            ];
            // Twitch-style carousel: shift the visible 4 by hlIdx so different categories
            // cycle through the featured row every 8s.
            const N = baseHighlights.length;
            const shifted = N > 4
              ? Array.from({ length: 4 }, (_, i) => baseHighlights[(hlIdx + i) % N])
              : baseHighlights.slice(0, 4);
            return shifted.map((h) => {
              const s = h.stream;
              const gameKey = h.game === "Live" ? "IRL" : h.game;
              const thumb = s?.thumbnail || FALLBACK_THUMBS[gameKey] || FALLBACK_THUMBS["IRL"];
              const target = s ? `/u/${s.creator_id}` : "/explore";
              const liveCount = h.pool_size || 0;
              return (
              <Link
                key={h.game}
                to={target}
                className="group block relative aspect-video overflow-hidden border border-white/10 hover:border-nexus-gold transition-colors"
                data-testid={`highlight-tile-${h.game.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <img src={thumb} alt={h.game} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                {s && s.livekit_room && <LivePreview streamId={s.id} fallback={null} />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-transparent" />
                <div className="absolute top-2 left-2 flex items-center gap-1.5">
                  {s
                    ? <span className="bg-nexus-crimson px-1.5 py-0.5 text-[9px] tracking-[0.25em] font-display font-bold uppercase animate-pulse">●LIVE</span>
                    : <span className="bg-white/10 px-1.5 py-0.5 text-[9px] tracking-[0.25em] font-display uppercase text-white/70">offline</span>}
                  <span className="bg-black/60 backdrop-blur-sm px-1.5 py-0.5 text-[9px] tracking-[0.2em] uppercase font-display text-white/80">{liveCount} live</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-3 space-y-1">
                  <div className="font-display text-[11px] tracking-[0.3em] uppercase text-nexus-gold">{gameKey}</div>
                  <div className="text-sm font-medium leading-snug line-clamp-1 group-hover:text-nexus-gold">{s?.title || "Tap to browse this category"}</div>
                  {s && <div className="text-[10px] text-white/60 tracking-wider">{s.creator_name} · {(s.viewers || 0).toLocaleString()} watching</div>}
                </div>
              </Link>
              );
            });
          })()}
        </div>

        {/* Extra categories — auto-scrolling Twitch-style carousel (paused on hover). */}
        <div className="mt-8">
          <div className="flex items-center justify-between mb-3">
            <div className="label-eyebrow text-white/60">// more categories</div>
            <div className="text-[9px] text-white/30 tracking-[0.3em] uppercase font-display hidden sm:block">// auto-scrolling · hover to pause</div>
          </div>
          <div className="nexus-marquee-mask" data-testid="extra-categories-rail">
            <div className="nexus-marquee-track flex gap-3">
              {[...categories, ...categories].map((c, i) => (
                <Link
                  key={`${c.id}-${i}`}
                  to={`/explore?category=${c.id}`}
                  className="group flex-none w-[240px] aspect-video relative overflow-hidden border border-white/10 hover:border-nexus-cyan transition"
                  data-testid={i < categories.length ? `extra-cat-${c.id}` : undefined}
                  aria-hidden={i >= categories.length}
                >
                  <img src={FALLBACK_THUMBS[c.label] || FALLBACK_THUMBS["IRL"]} alt={c.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="font-display text-[11px] tracking-[0.3em] uppercase text-nexus-cyan">{c.label}</div>
                    <div className="text-[10px] text-white/60">Browse category</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === CHOOSE YOUR TIER === */}
      <section className="space-y-6" data-testid="tier-picker">
        <div className="text-center space-y-2">
          <div className="label-eyebrow text-nexus-cyan">// membership</div>
          <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tighter">Choose your tier</h2>
          <p className="text-white/50 max-w-xl mx-auto text-sm">Start free with every social + monetisation core. Upgrade to Studio when you&apos;re ready to broadcast at pro-level.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 lg:gap-6">
          {/* ---- FREE TIER ---- */}
          <div className="relative glass border border-white/10 rounded-2xl p-6 lg:p-8 space-y-5 hover:border-white/25 transition-colors" data-testid="tier-free">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-eyebrow text-white/60">// tier · 01</div>
                <div className="font-display font-black text-2xl lg:text-3xl mt-1">Free</div>
              </div>
              <div className="text-right">
                <div className="font-display font-black text-2xl text-white">$0</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/40">forever</div>
              </div>
            </div>
            <p className="text-xs text-white/60">Everything a creator needs to grow a community, earn commission and monetise from day one.</p>

            <ul className="space-y-2 text-sm text-white/80" data-testid="tier-free-features">
              {[
                "Own profile with newsfeed & clips",
                "Snapchat-style DMs & messenger",
                "1-tap video calls with PiP & ringtones",
                "Ad campaigns via Business Suite",
                "Referral codes · commission on every Studio upgrade",
                "Buy Nexus Coins (NX) to send stickers",
                "Purchase supporter subscriptions to backing creators",
                "Earn from watching reels, live streams & ads",
                "Boost your posts into paid ad slots",
                "Mobile live streaming (in-app camera)",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 text-nexus-cyan flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <Link to="/auth" className="btn-ghost w-full justify-center !py-3" data-testid="tier-free-cta">
              Sign up Free
            </Link>
          </div>

          {/* ---- STUDIO TIER ---- */}
          <div className="relative glass border border-nexus-cyan/40 rounded-2xl p-6 lg:p-8 space-y-5 overflow-hidden hover:border-nexus-cyan transition-colors" data-testid="tier-studio">
            {/* Cyan glow accent */}
            <div className="absolute inset-0 -z-0 pointer-events-none opacity-40" style={{ background: "radial-gradient(ellipse at top right, rgba(0,240,255,0.12), transparent 60%)" }} />
            <div className="absolute top-4 right-4 text-[9px] tracking-[0.3em] uppercase font-display bg-nexus-cyan/15 text-nexus-cyan px-2 py-1 border border-nexus-cyan/30 rounded">
              pro creator
            </div>

            <div className="relative">
              <div className="label-eyebrow text-nexus-cyan">// tier · 02</div>
              <div className="font-display font-black text-2xl lg:text-3xl mt-1 flex items-center gap-2">
                Studio <Sparkles className="w-5 h-5 text-nexus-cyan" />
              </div>
              <p className="text-xs text-white/60 mt-2">Everything in Free — plus the tools that turn a channel into a broadcast.</p>
            </div>

            <div className="relative space-y-3">
              <div className="text-[10px] tracking-[0.25em] uppercase text-white/40">// includes everything in Free, plus</div>
              <ul className="space-y-2 text-sm text-white/85" data-testid="tier-studio-features">
                <li className="flex items-start gap-2">
                  <Radio className="w-4 h-4 mt-0.5 text-nexus-cyan flex-shrink-0" />
                  <span><strong className="text-white">OBS live streaming</strong> · RTMP ingress for pro multi-scene broadcasts</span>
                </li>
                <li className="flex items-start gap-2">
                  <Shirt className="w-4 h-4 mt-0.5 text-nexus-cyan flex-shrink-0" />
                  <span>
                    <strong className="text-white">Elysium Wear integration</strong>
                    <span className="ml-2 text-[9px] tracking-[0.25em] uppercase text-nexus-amber bg-nexus-amber/10 px-1.5 py-0.5 rounded">coming soon</span>
                    <div className="text-[11px] text-white/50 mt-0.5">Merch drops linked to your channel</div>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Video className="w-4 h-4 mt-0.5 text-nexus-cyan flex-shrink-0" />
                  <span>
                    <strong className="text-white">BReels video reels</strong>
                    <span className="ml-2 text-[9px] tracking-[0.25em] uppercase text-nexus-amber bg-nexus-amber/10 px-1.5 py-0.5 rounded">coming soon</span>
                    <div className="text-[11px] text-white/50 mt-0.5">AI-stitched Sora-2 shorts inside your channel</div>
                  </span>
                </li>
              </ul>
            </div>

            <Link to="/auth?plan=studio" className="btn-nexus w-full justify-center !py-3 relative" data-testid="tier-studio-cta">
              <Sparkles className="w-4 h-4" /> Start Studio
            </Link>
          </div>
        </div>
      </section>

      {/* === BOTTOM CTA === */}
      <section className="relative overflow-hidden p-12 lg:p-20 border border-white/10 text-center">
        <div className="absolute inset-0 cinematic-edge" />
        <div className="relative space-y-6">
          <div className="label-eyebrow text-nexus-gold">// monetisation</div>
          <h2 className="font-display font-black text-3xl lg:text-6xl tracking-tighter">Streams. Stickers. Subs.<br /><span className="nexus-gradient-text">All simultaneous.</span></h2>
          <p className="text-white/50 max-w-2xl mx-auto">Tip the creators you love. Sponsor the streams you trust. Build your channel.</p>
          <Link to="/auth" className="btn-nexus" data-testid="cta-bottom">Sign up</Link>
        </div>
      </section>
    </div>
  );
}
